import type { FunctionComponent } from "react";
import styled from "styled-components";
import { AdUnit, TextAd, ShoppingAd } from "app/components/React/Table/cells/AdUnit";
import {
    AdPreview,
    H3Container,
    AdTargetUrl,
    AdDescription,
    AdUnitComponent,
    ImageContainer,
} from "app/components/React/Table/cells/AdUnitStyles";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { i18nFilter } from "app/filters/i18nFilter";
import { adsTargetURL } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";

export enum EAdType {
    PLA = "pla",
    PAID = "paid",
    PAID_SITELINKS = "paid_sitelinks",
}

const onClickShoppingAd = (targetUrl: string) => window.open(targetUrl, "_blank").focus();
const onClickTextAd = (DestUrl) => allTrackers.dispatchTrackEvent("External Link", "click", `Table/Ad/${DestUrl}`);

const TextAdWrapper = styled.div`
    width: 100%;
    ${AdUnitComponent} {
        height: 100%;
        justify-content: center;
        ${AdPreview} {
            font-size: 14px;
            margin-bottom: 4px;
        }
        ${H3Container} {
            ${AdTargetUrl} {
                max-width: 100%;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                overflow-wrap: break-word;
                display: -webkit-box;
                align-self: center;
                font-size: 16px;
                line-height: 24px;
                font-family: "DM Sans";
            }
            padding-bottom: unset;
            margin-bottom: 4px;
            display: flex;
        }
        ${AdDescription} {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            overflow-wrap: break-word;
            font-size: 14px;
            line-height: 20px !important;
        }
    }
`;

const ShoppingAdWrapper = styled.div<{ fixedImageHeight?: boolean }>`
    ${({ fixedImageHeight = true }) =>
        fixedImageHeight &&
        `
    ${ImageContainer} {
        height: 116px;
        min-height: unset;
    }`}
`;

export const AdColumnUnit: FunctionComponent<ITableCellProps & { fixedImageHeight?: boolean }> = (props) => {
    const { fixedImageHeight } = props;
    const targetUrl = adsTargetURL()(props.value);
    const {
        Title,
        Description,
        AdType,
        DisplayUrl,
        DestUrl,
        DestUrlFavicon,
        DestUrlFaviconIconType,
        Price,
        Brand,
        ImageUrl,
    } = props.value;

    if (Title === "grid.upgrade") {
        return <AdUnit {...props} />;
    }

    switch (AdType) {
        case EAdType.PAID_SITELINKS:
            return (
                <TextAdWrapper>
                    <TextAd
                        Title={Title}
                        Description={Description}
                        targetUrl={targetUrl}
                        onClick={() => onClickTextAd(DestUrl)}
                        withBorder={false}
                        withPadding={false}
                    />
                </TextAdWrapper>
            );
        case EAdType.PAID:
            return (
                <TextAdWrapper>
                    <TextAd
                        targetUrl={targetUrl}
                        onClick={() => onClickTextAd(DestUrl)}
                        withBorder={false}
                        withPadding={false}
                        Title={Title}
                        Description={Description}
                        DestUrl={DisplayUrl}
                        DestUrlFavicon={DestUrlFavicon}
                        DestUrlFaviconIconType={DestUrlFaviconIconType}
                        DestUrlPrefix={i18nFilter()("pla.ads.page.ads.table.columns.addetails.ad.prefix")}
                    />
                </TextAdWrapper>
            );
        case EAdType.PLA:
            return (
                <ShoppingAdWrapper fixedImageHeight={fixedImageHeight}>
                    <ShoppingAd
                        Title={Title}
                        Price={Price}
                        Brand={Brand}
                        ImageUrl={ImageUrl}
                        onImageClick={() => onClickShoppingAd(targetUrl)}
                        onNoImageClick={() => onClickShoppingAd(targetUrl)}
                    />
                </ShoppingAdWrapper>
            );
    }
};
